import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { Button, Field, Notice } from '../../design-system/components';
import type { ContentBlock } from '../../types/contracts';
const AUTOMATIC_KEYS = new Set(['price', 'payment', 'deadline', 'revisions', 'meetings', 'compatibility', 'conditions', 'exclusions']);
export function ContentEditor({ id, onBack, onFinalize, onEditPrice }: { id: string; onBack: () => void; onFinalize: () => void; onEditPrice: () => void }) {
  const [blocks, setBlocks] = useState<ContentBlock[]>([]),
    [error, setError] = useState(''),
    [saved, setSaved] = useState(''),
    [newOpen, setNewOpen] = useState(false),
    [editing, setEditing] = useState<Record<string, boolean>>({}),
    [dirty, setDirty] = useState<Record<string, boolean>>({});
  async function load() {
    try {
      setBlocks(await api.get(`/proposals/${id}/content-blocks`));
    } catch (e: any) {
      setError(e.message);
    }
  }
  useEffect(() => {
    load();
  }, [id]);
  async function patch(block: ContentBlock, body: any) {
    try {
      await api.patch(`/proposals/${id}/content-blocks/${block.id}`, body);
      setDirty((current) => ({ ...current, [block.id]: false }));
      await load();
      setSaved('Conteúdo salvo.');
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function remove(block: ContentBlock) {
    await api.delete(`/proposals/${id}/content-blocks/${block.id}`);
    await load();
  }
  async function restore(block: ContentBlock) {
    await api.post(`/proposals/${id}/content-blocks/${block.id}/restore`);
    setEditing((x) => ({ ...x, [block.id]: false }));
    await load();
  }
  async function removeExclusionConcept(block: ContentBlock, conceptKey: string) {
    try {
      const removed = Array.from(new Set([...(block.exclusion_removed_concept_keys || []), conceptKey]));
      await api.put(`/proposals/${id}/exclusion-concepts`, { removed_concept_keys: removed });
      await load();
      setSaved('Conceito removido e bullet recomposto.');
    } catch (e: any) { setError(e.message); }
  }
  async function restoreExclusionConcepts() {
    try {
      await api.put(`/proposals/${id}/exclusion-concepts`, { removed_concept_keys: [] });
      await load();
      setSaved('Conceitos padrão restaurados.');
    } catch (e: any) { setError(e.message); }
  }
  async function resolve(block: ContentBlock, choice: 'automatic' | 'manual' | 'remove' | 'review') {
    await api.post(`/proposals/${id}/content-blocks/${block.id}/resolve`, { choice });
    if (choice === 'review') setEditing((x) => ({ ...x, [block.id]: true }));
    await load();
  }
  async function add(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    await api.post(`/proposals/${id}/content-blocks`, {
      title: String(f.get('title')),
      text: String(f.get('text')),
      export_class: String(f.get('export_class')),
    });
    setNewOpen(false);
    await load();
  }
  async function saveDirtyBlocks() {
    const pending = blocks.filter((block) => dirty[block.id]);
    for (const block of pending) {
      await api.patch(`/proposals/${id}/content-blocks/${block.id}`, { final_text: block.final_text });
    }
    if (pending.length) {
      setDirty({});
      setSaved('Conteúdo salvo.');
    }
  }
  async function returnToConstruction(openPrice: boolean) {
    try {
      await saveDirtyBlocks();
      if (openPrice) onEditPrice(); else onBack();
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function finish() {
    try {
      await saveDirtyBlocks();
      await api.put(`/proposals/${id}/stage`, { stage: 'finalizar' });
      onFinalize();
    } catch (e: any) {
      setError(e.message);
    }
  }
  const conflicts = blocks.filter((b) => ['pending', 'orphaned'].includes(b.conflict_state));
  return (
    <div className="content-editor">
      <header className="flow-head" tabIndex={-1}>
        <span className="eyebrow">EDITAR CONTEÚDO</span>
        <h2>Conteúdo da proposta</h2>
        <p>Revise e ajuste os textos gerados a partir das informações selecionadas.</p>
      </header>
      {error && (
        <Notice tone="danger" title="Ação não concluída">
          {error}
        </Notice>
      )}
      {saved && <div className="inline-save">{saved}</div>}
      {conflicts.length > 0 && (
        <Notice tone="warning" title="Há conteúdos que precisam de decisão">
          Escolha atualizar com os dados da construção, manter a redação personalizada ou comparar as alterações.
        </Notice>
      )}
      <div className="editor-toolbar">
        <Button variant="secondary" onClick={() => returnToConstruction(false)}>
          Voltar à construção
        </Button>
        <Button variant="secondary" onClick={() => setNewOpen(true)}>
          Incluir bloco
        </Button>
        <Button onClick={finish} disabled={conflicts.length > 0}>
          Finalizar proposta
        </Button>
      </div>
      <div className="content-blocks">
        {blocks.map((b, index) => {
          const financialBlock = b.block_key === 'price' || b.block_key === 'payment';
          const automatic = financialBlock || (AUTOMATIC_KEYS.has(b.block_key) && !b.manually_edited);
          const open = financialBlock ? false : Boolean(editing[b.id]) || !automatic;
          const visible = b.resolved_text || b.final_text;
          return (
            <article key={b.id} className={`content-block ${['pending', 'orphaned'].includes(b.conflict_state) ? 'has-conflict' : ''}`}>
              <header>
                <div>
                  <span className="block-type">{b.include_in_pdf ? 'Incluído no PDF' : 'Fora do PDF'}</span>
                  <h3>{b.title}</h3>
                </div>
                {!financialBlock && <div className="block-controls">
                  <button onClick={() => patch(b, { position: b.position - 15 })} disabled={index === 0}>
                    ↑
                  </button>
                  <button onClick={() => patch(b, { position: b.position + 15 })} disabled={index === blocks.length - 1}>
                    ↓
                  </button>
                  <button onClick={() => restore(b)}>Restaurar</button>
                  <button onClick={() => remove(b)}>Excluir</button>
                </div>}
              </header>
              {b.conflict_state === 'orphaned' && (
                <div className="conflict-panel">
                  <strong>A informação que originou este conteúdo foi removida da construção.</strong>
                  <div>
                    <Button variant="secondary" onClick={() => resolve(b, 'remove')}>
                      Remover o bloco
                    </Button>
                    <Button variant="secondary" onClick={() => resolve(b, 'manual')}>
                      Manter como texto manual
                    </Button>
                    <Button variant="secondary" onClick={() => resolve(b, 'review')}>
                      Revisar agora
                    </Button>
                  </div>
                </div>
              )}
              {b.conflict_state === 'pending' && (
                <div className="conflict-panel">
                  <strong>Os dados usados para gerar este conteúdo foram alterados.</strong>
                  <div className="conflict-columns">
                    <div>
                      <small>Redação personalizada</small>
                      <p>{b.final_text}</p>
                    </div>
                    <div>
                      <small>Dados atuais da construção</small>
                      <p>{b.suggested_text}</p>
                    </div>
                  </div>
                  <div>
                    <Button variant="secondary" onClick={() => resolve(b, 'automatic')}>
                      Atualizar com os dados da construção
                    </Button>
                    <Button variant="secondary" onClick={() => resolve(b, 'manual')}>
                      Manter redação personalizada
                    </Button>
                    <Button variant="secondary" onClick={() => resolve(b, 'review')}>
                      Comparar alterações
                    </Button>
                  </div>
                </div>
              )}
              {!open ? (
                <div className="automatic-block-view">
                  <div className="generated-copy">
                    {visible.split('\n').map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                  {b.linked_value_brl && <small className="linked-value-hint">Preço comercial vigente: {b.linked_value_brl}</small>}
                  {b.block_key === 'exclusions' && b.exclusion_concepts && (
                    <div className="exclusion-concept-editor">
                      <strong>Conceitos ativos do Não-escopo</strong>
                      <p>Remova somente o conceito necessário; o texto e a pontuação serão recompostos automaticamente.</p>
                      <div className="exclusion-concept-list">
                        {b.exclusion_concepts.map((concept) => (
                          <div key={concept.concept_key}>
                            <span>{concept.text_fragment}</span>
                            {concept.removable !== false && <button type="button" onClick={() => removeExclusionConcept(b, concept.concept_key)}>Remover conceito</button>}
                          </div>
                        ))}
                      </div>
                      {(b.exclusion_removed_concept_keys || []).length > 0 && <Button variant="ghost" onClick={restoreExclusionConcepts}>Restaurar conceitos removidos</Button>}
                    </div>
                  )}
                  {financialBlock ? (
                    <>
                      <div className="price-readonly-note">Conteúdo financeiro proveniente da construção da proposta.</div>
                      <Button variant="secondary" onClick={() => returnToConstruction(true)}>
                        Editar preço e pagamento na construção da proposta
                      </Button>
                    </>
                  ) : (
                    <Button variant="secondary" onClick={() => setEditing((x) => ({ ...x, [b.id]: true }))}>
                      Personalizar redação
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <textarea
                    aria-label={`Conteúdo de ${b.title}`}
                    value={b.final_text}
                    onChange={(e) => { setBlocks((old) => old.map((x) => (x.id === b.id ? { ...x, final_text: e.target.value } : x))); setDirty((current) => ({ ...current, [b.id]: true })); }}
                    onBlur={(e) => patch(b, { final_text: e.currentTarget.value })}
                  />
                  {AUTOMATIC_KEYS.has(b.block_key) && (
                    <Button variant="ghost" onClick={() => restore(b)}>
                      Atualizar com os dados da construção
                    </Button>
                  )}
                </>
              )}
              <footer>
                {!financialBlock && <label>
                  <input
                    type="checkbox"
                    checked={Boolean(b.include_in_pdf)}
                    onChange={(e) => patch(b, { include_in_pdf: e.target.checked })}
                  />
                  Incluir no PDF
                </label>}
                <span>{financialBlock ? 'Conteúdo financeiro automático' : b.manually_edited ? 'Redação personalizada' : 'Conteúdo automático'}</span>
              </footer>
            </article>
          );
        })}
      </div>
      {newOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <form className="modal" onSubmit={add}>
            <h2>Incluir bloco</h2>
            <Field id="block-title" label="Título" required>
              <input id="block-title" name="title" required />
            </Field>
            <Field id="block-text" label="Conteúdo">
              <textarea id="block-text" name="text" />
            </Field>
            <Field id="block-export" label="Destino">
              <select id="block-export" name="export_class">
                <option value="CLIENTE">Cliente</option>
                <option value="AMBAS">Interno e cliente</option>
                <option value="INTERNA">Somente interno</option>
              </select>
            </Field>
            <div className="modal-actions">
              <Button type="button" variant="secondary" onClick={() => setNewOpen(false)}>
                Cancelar
              </Button>
              <Button>Adicionar</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
